import { createSlice } from '@reduxjs/toolkit';
import { axiosAmApiInstance as amApi } from '../../utils/axios';
import { dispatch } from '../store';
import { rejectWithError } from '../dispatchError';
import {
  BillingConfigurationSettings,
  BillingConfigurationState
} from '../../@types/billingConfiguration';
import { translator } from 'translation/useTranslator';

const initialState: BillingConfigurationState = {
  loading: {},
  configuration: null,
  fetched: false
};

const slice = createSlice({
  name: 'billingConfiguration',
  initialState,
  reducers: {
    startLoading(state, action) {
      state.loading[action.payload] = true;
    },
    stopLoading(state, action) {
      state.loading[action.payload] = false;
    },
    getBillingConfigurationSuccess(state, action) {
      state.configuration = action.payload;
      state.fetched = true;
      state.loading.configuration = false;
    },
    getBillingConfigurationNotFound(state) {
      state.configuration = null;
      state.fetched = true;
      state.loading.configuration = false;
    }
  }
});

export function getBillingConfiguration() {
  return async () => {
    dispatch(slice.actions.startLoading('configuration'));
    try {
      const response = await amApi.get('/billing-configuration', {
        validateStatus: (status) => status === 200 || status === 404
      });

      if (response.status === 404) {
        dispatch(slice.actions.getBillingConfigurationNotFound());
      } else {
        dispatch(slice.actions.getBillingConfigurationSuccess(response.data));
      }

      return await Promise.resolve({
        result: response,
        defaultSuccessMessage:
          translator?.billingConfigurationFetched() ?? 'Billing configuration fetched'
      });
    } catch (error) {
      dispatch(slice.actions.stopLoading('configuration'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotFetchBillingConfiguration() ??
          'Could not fetch the billing configuration'
      });
    }
  };
}

export function createBillingConfiguration(settings: BillingConfigurationSettings) {
  return async () => {
    dispatch(slice.actions.startLoading('save'));
    try {
      await amApi.post('/billing-configuration', settings);
      dispatch(getBillingConfiguration());
      dispatch(slice.actions.stopLoading('save'));

      return await Promise.resolve({
        defaultSuccessMessage:
          translator?.billingConfigurationCreated() ?? 'Billing configuration created'
      });
    } catch (error) {
      dispatch(slice.actions.stopLoading('save'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotCreateBillingConfiguration() ??
          'Could not create the billing configuration'
      });
    }
  };
}

export function updateBillingConfiguration(settings: BillingConfigurationSettings) {
  return async () => {
    dispatch(slice.actions.startLoading('save'));
    try {
      const response = await amApi.put('/billing-configuration', settings);
      dispatch(getBillingConfiguration());
      dispatch(slice.actions.stopLoading('save'));

      return await Promise.resolve({
        result: response,
        defaultSuccessMessage:
          response.data?.changesStaged === true
            ? translator?.billingConfigurationChangesStaged() ??
              'The changes are staged and take effect at the next billing cycle'
            : translator?.billingConfigurationUpdated() ?? 'Billing configuration updated'
      });
    } catch (error) {
      dispatch(slice.actions.stopLoading('save'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotUpdateBillingConfiguration() ??
          'Could not update the billing configuration'
      });
    }
  };
}

export default slice.reducer;
